import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle2, AlertCircle, CreditCard, Lock, Percent } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// BATCH 6C Part 1 — gateway fee % used to split every gateway-collected
// receipt into Gross / Fee / Net (routes/payments.py _auto_record_gateway_receipt).
const GATEWAY_FEE_LABELS = { stripe: 'Stripe', razorpay: 'Razorpay', cashfree: 'Cashfree', payu: 'PayU' };

export default function PaymentGatewaySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    defaultGateway: 'cashfree',
    razorpayKeyId: '',
    razorpayKeySecret: '',
    cashfreeAppId: '',
    cashfreeSecretKey: '',
    cashfreeEnvironment: 'sandbox',
    stripeConfigured: false,
    razorpayConfigured: false,
    cashfreeConfigured: false,
  });
  const [feePercents, setFeePercents] = useState({});
  const [savingFees, setSavingFees] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/payments/settings`, { withCredentials: true });
      setData((d) => ({ ...d, ...res.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeePercents = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/gateway-fee-percents`, { withCredentials: true });
      setFeePercents(res.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load gateway fee percentages');
    }
  };

  useEffect(() => { fetchSettings(); fetchFeePercents(); }, []);

  const handleSaveFeePercents = async () => {
    setSavingFees(true);
    try {
      const res = await axios.put(`${API}/api/settings/gateway-fee-percents`, feePercents, { withCredentials: true });
      setFeePercents(res.data || {});
      toast.success('Gateway fee percentages saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingFees(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        defaultGateway: data.defaultGateway,
        cashfreeEnvironment: data.cashfreeEnvironment,
      };
      if (data.razorpayKeyId && !data.razorpayKeyId.startsWith('rzp_masked')) payload.razorpayKeyId = data.razorpayKeyId;
      if (data.razorpayKeySecret && !data.razorpayKeySecret.startsWith('****')) payload.razorpayKeySecret = data.razorpayKeySecret;
      if (data.cashfreeAppId) payload.cashfreeAppId = data.cashfreeAppId;
      if (data.cashfreeSecretKey && !data.cashfreeSecretKey.startsWith('****')) payload.cashfreeSecretKey = data.cashfreeSecretKey;
      await axios.put(`${API}/api/payments/settings`, payload, { withCredentials: true });
      toast.success('Payment settings saved');
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="payment-gateway-settings">
      {/* Default Gateway */}
      <Card className="p-6 sm:p-8 bg-white border border-slate-200 rounded-2xl">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-teal-700" /> Default Payment Gateway
        </h3>
        <Label className="text-xs text-slate-500 mb-2 block">Which gateway to use when creating new payment links</Label>
        <Select value={data.defaultGateway} onValueChange={(v) => setData({ ...data, defaultGateway: v })}>
          <SelectTrigger data-testid="default-gateway-select" className="w-60"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cashfree" disabled={!data.cashfreeConfigured}>Cashfree {!data.cashfreeConfigured && '(not configured)'}</SelectItem>
            <SelectItem value="razorpay" disabled={!data.razorpayConfigured}>Razorpay {!data.razorpayConfigured && '(not configured)'}</SelectItem>
            <SelectItem value="stripe" disabled={!data.stripeConfigured}>Stripe {!data.stripeConfigured && '(not configured)'}</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {/* Cashfree */}
      <Card className="p-6 sm:p-8 bg-white border border-slate-200 rounded-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              Cashfree
              {data.cashfreeConfigured ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid="cashfree-configured" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-500" />
              )}
            </h3>
            <p className="text-xs text-slate-500 mt-1">Indian payment gateway · UPI, Cards, NetBanking, Wallets, EMI</p>
          </div>
          <a
            href="https://merchant.cashfree.com/merchants/pg/api-integration"
            target="_blank" rel="noreferrer"
            className="text-xs text-teal-700 hover:underline"
          >Get API keys →</a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-slate-600">App ID</Label>
            <Input
              data-testid="cashfree-app-id-input"
              value={data.cashfreeAppId}
              onChange={(e) => setData({ ...data, cashfreeAppId: e.target.value })}
              placeholder="e.g. 707655371d96a39f59075fce83556707"
              className="font-mono text-sm"
            />
          </div>
          <div>
            <Label className="text-sm text-slate-600 flex items-center gap-1">
              Secret Key <Lock className="w-3 h-3 text-slate-400" />
            </Label>
            <Input
              data-testid="cashfree-secret-input"
              type="password"
              value={data.cashfreeSecretKey}
              onChange={(e) => setData({ ...data, cashfreeSecretKey: e.target.value })}
              placeholder={data.cashfreeConfigured ? 'Leave blank to keep existing' : 'cfsk_...'}
              className="font-mono text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm text-slate-600">Environment</Label>
            <Select
              value={data.cashfreeEnvironment}
              onValueChange={(v) => setData({ ...data, cashfreeEnvironment: v })}
            >
              <SelectTrigger data-testid="cashfree-env-select" className="w-60"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                <SelectItem value="production">Production (live)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1.5">
              Sandbox keys differ from production keys — get sandbox-specific keys from the Cashfree dashboard.
            </p>
          </div>
        </div>
      </Card>

      {/* Razorpay */}
      <Card className="p-6 sm:p-8 bg-white border border-slate-200 rounded-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              Razorpay
              {data.razorpayConfigured ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-500" />
              )}
            </h3>
            <p className="text-xs text-slate-500 mt-1">Alternative Indian gateway · UPI, Cards, NetBanking</p>
          </div>
          <a
            href="https://dashboard.razorpay.com/app/keys"
            target="_blank" rel="noreferrer"
            className="text-xs text-teal-700 hover:underline"
          >Get API keys →</a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-slate-600">Key ID</Label>
            <Input
              data-testid="razorpay-key-id-input"
              value={data.razorpayKeyId}
              onChange={(e) => setData({ ...data, razorpayKeyId: e.target.value })}
              placeholder="rzp_live_... or rzp_test_..."
              className="font-mono text-sm"
            />
          </div>
          <div>
            <Label className="text-sm text-slate-600 flex items-center gap-1">
              Key Secret <Lock className="w-3 h-3 text-slate-400" />
            </Label>
            <Input
              data-testid="razorpay-secret-input"
              type="password"
              value={data.razorpayKeySecret}
              onChange={(e) => setData({ ...data, razorpayKeySecret: e.target.value })}
              placeholder={data.razorpayConfigured ? 'Leave blank to keep existing' : '••••••••'}
              className="font-mono text-sm"
            />
          </div>
        </div>
      </Card>

      {/* Stripe */}
      <Card className="p-6 sm:p-8 bg-white border border-slate-200 rounded-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              Stripe
              {data.stripeConfigured ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-500" />
              )}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Stripe is configured via environment variable (STRIPE_API_KEY) — not via this UI.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-teal-700 hover:bg-teal-800 text-white px-8 rounded-xl"
          data-testid="save-payment-settings-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving</> : 'Save Changes'}
        </Button>
      </div>

      {/* BATCH 6C Part 1 — Gateway Fee Percentages */}
      <Card className="p-6 sm:p-8 bg-white border border-slate-200 rounded-2xl" data-testid="gateway-fee-percents-card">
        <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Percent className="w-5 h-5 text-teal-700" /> Gateway Fee Percentages
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Used to split every gateway-collected receipt into Gross / Fee / Net (never folded silently into the collected amount).
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.keys(GATEWAY_FEE_LABELS).map((gw) => (
            <div key={gw}>
              <Label className="text-sm text-slate-600">{GATEWAY_FEE_LABELS[gw]} (%)</Label>
              <Input
                type="number"
                step="0.01"
                data-testid={`gateway-fee-percent-${gw}`}
                value={feePercents[gw] ?? ''}
                onChange={(e) => setFeePercents({ ...feePercents, [gw]: e.target.value })}
                className="font-mono text-sm mt-1"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSaveFeePercents}
            disabled={savingFees}
            variant="outline"
            className="px-6 rounded-xl"
            data-testid="save-gateway-fee-percents-btn"
          >
            {savingFees ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving</> : 'Save Fee Percentages'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
